import AddIcon from '@mui/icons-material/Add';
import { Button, Stack, Tooltip, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import { fontWeight } from '@verantos-dev/shared-ui-components/tokens';

import { DatasetViewToggle } from './DatasetViewToggle';
import { TableShell } from './TableShell';
import { VariableSourceList } from './VariableSourceList';
import { AddVariablePickerModal } from '../containers/definition/AddVariablePickerModal';
import { AnalyticalDatasetResults } from '../containers/results/AnalyticalDatasetResults';
import { AnalyticalDatasetResultsEmpty } from '../containers/results/AnalyticalDatasetResultsEmpty';
import type { TableDataPreviewProps } from '../types';

// Renders the designed table view (TableShell) when a real table is
// selected -- filled with the latest run's cells when results exist for it,
// definition-only otherwise. The "Analytical dataset" option has no table
// shape of its own: it always offers both its structural variable listing
// and its results grid via the List/Table toggle, defaulting to whichever
// one `useTablesSection` picks (Table once results exist, List otherwise).
// Selecting Table before results exist shows an empty grid shaped like the
// real one (same columns, no rows) instead.
export const TableDataPreview = ({
  selectedTable,
  selectedTableNumber,
  isAnalyticalDatasetSelected,
  selectedDatasetSources,
  analyticalDatasetCount,
  sources,
  projectId,
  addVariableLabel,
  isVariablePickerOpen,
  onAddVariable,
  onCloseVariablePicker,
  isEditDisabled,
  editLockTooltip,
  analyticalDatasetProfileId,
  hasAnalyticalDatasetResults,
  datasetSourcesMissingFromRun,
  datasetView,
  onSelectDatasetView,
  listViewLabel,
  tableViewLabel,
  datasetEmptyHint,
}: TableDataPreviewProps) => {
  const { formatMessage } = useIntl();

  if (!selectedTable && !isAnalyticalDatasetSelected) return null;

  if (selectedTable) {
    return <TableShell table={selectedTable} tableNumber={selectedTableNumber} sources={sources} />;
  }

  const isShowingResultsGrid =
    datasetView === 'table' && hasAnalyticalDatasetResults && Boolean(analyticalDatasetProfileId);

  return (
    <Stack spacing={4} sx={datasetView === 'table' ? { flex: 1, minHeight: 0 } : undefined}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: fontWeight.semibold }}>
          {`${formatMessage({
            id: 'analysisDetail.tables.analyticalVariables',
          })} (${analyticalDatasetCount})`}
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <DatasetViewToggle
            view={datasetView}
            onChange={onSelectDatasetView}
            listLabel={listViewLabel}
            tableLabel={tableViewLabel}
          />
          <Tooltip title={editLockTooltip ?? ''} disableHoverListener={!editLockTooltip}>
            {/* span wrapper so the tooltip still shows when the button is disabled */}
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={onAddVariable}
                disabled={isEditDisabled}
                startIcon={<AddIcon fontSize="small" />}
                sx={{ textTransform: 'none' }}
                data-testid="analysis-variable-add"
              >
                {addVariableLabel}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
      {datasetView === 'table' ? (
        isShowingResultsGrid && analyticalDatasetProfileId ? (
          <AnalyticalDatasetResults
            projectId={projectId}
            analyticalDatasetProfileId={analyticalDatasetProfileId}
            hasAnalyticalVariables={selectedDatasetSources.length > 0}
            sourcesMissingFromRun={datasetSourcesMissingFromRun}
          />
        ) : (
          <AnalyticalDatasetResultsEmpty
            datasetSources={selectedDatasetSources}
            message={datasetEmptyHint}
          />
        )
      ) : (
        <VariableSourceList datasetSources={selectedDatasetSources} />
      )}
      {isVariablePickerOpen && (
        <AddVariablePickerModal projectId={projectId} onClose={onCloseVariablePicker} />
      )}
    </Stack>
  );
};
